import { createContext, ReactNode, useContext, useMemo, useState } from "react"
import { AppId } from "../../app/appTypes"

export type NoteColor = "GREEN" | "YELLOW" | "BLUE" | "PINK" | "ORANGE" | "PURPLE" | "RED" | "GRAY"

export type NoteTarget = {
    objectType: string
    objectGuid: AppId
    label: string
}

export type UserNote = {
    id: string
    screenGuid: AppId
    objectType: string
    objectGuid: AppId | null
    objectLabel: string
    content: string
    color: NoteColor
    visibility: "PRIVATE"
    createDate: string
    modifyDate?: string
}

type UserNotesContextValue = {
    notes: UserNote[]
    noteCount: number
    open: boolean
    target: NoteTarget | null
    openNotes: (target: NoteTarget | null) => void
    closeNotes: () => void
    countNotesFor: (objectType: string, objectGuid: AppId) => number
    addNote: (target: NoteTarget, content: string, color: NoteColor) => void
    updateNote: (id: string, content: string, color: NoteColor) => void
    deleteNote: (id: string) => void
}

const UserNotesContext = createContext<UserNotesContextValue | null>(null)

export const useUserNotes = () => {
    const context = useContext(UserNotesContext)
    if (!context) throw new Error("UserNotesProvider is required")
    return context
}

export const UserNotesProvider = ({ screenGuid, children }: { screenGuid: AppId, children: ReactNode }) => {
    const [allNotes, setAllNotes] = useState<UserNote[]>([])
    const [open, setOpen] = useState(false)
    const [target, setTarget] = useState<NoteTarget | null>(null)
    const notes = useMemo(() => allNotes.filter(note => note.screenGuid === screenGuid), [allNotes, screenGuid])

    const openNotes = (nextTarget: NoteTarget | null) => {
        setTarget(nextTarget)
        setOpen(true)
    }
    const closeNotes = () => setOpen(false)
    const countNotesFor = (objectType: string, objectGuid: AppId) =>
        notes.filter(note => note.objectType === objectType && note.objectGuid === objectGuid).length
    const addNote = (noteTarget: NoteTarget, content: string, color: NoteColor) => {
        setAllNotes(current => [{
            id: crypto.randomUUID(),
            screenGuid,
            objectType: noteTarget.objectType,
            objectGuid: noteTarget.objectGuid,
            objectLabel: noteTarget.label,
            content,
            color,
            visibility: "PRIVATE",
            createDate: new Date().toISOString(),
        }, ...current])
    }
    const updateNote = (id: string, content: string, color: NoteColor) => {
        setAllNotes(current => current.map(note => note.screenGuid === screenGuid && note.id === id
            ? { ...note, content, color, modifyDate: new Date().toISOString() }
            : note))
    }
    const deleteNote = (id: string) => {
        setAllNotes(current => current.filter(note => note.screenGuid !== screenGuid || note.id !== id))
    }

    return <UserNotesContext.Provider value={{
        notes, noteCount: notes.length, open, target, openNotes, closeNotes, countNotesFor, addNote, updateNote, deleteNote,
    }}>
        {children}
    </UserNotesContext.Provider>
}
